import { tracking } from '@iwoca/frontend-tracking-library';

import {
  checkHasThirtyDayAndThreeMonthLimit,
  formatDuration,
} from './duration.helpers';
import { RefreshExpiredLimits } from './RefreshExpiredLimits';
import { SpendingLimitBar } from './SpendingLimitBar';
import { useGetBuyerSpendingLimit } from '../../../../../api/lending/lapiHooks';
import { daysSinceStatusDate } from '../../../Dashboard.helpers';
import { Application } from '../../../Dashboard.types';
import { useGetSpendingLimits } from '../../../hooks/useGetSpendingLimits';
import { formatCurrency } from '../../../utils/currency';

export const DeclinedLimits = ({
  setIsSpendingLimitDrawerOpen,
  refreshSpendingLimit,
  loading,
  application,
}: {
  setIsSpendingLimitDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSpendingLimit: () => void;
  loading: boolean;
  application: Application;
}) => {
  const { spendingLimit, loadingSpendingLimit } = useGetBuyerSpendingLimit();
  const { spendingLimits } = useGetSpendingLimits();

  const activeSpendingLimit = spendingLimit?.data?.active_spending_limit;

  const hasThirtyDayAndThreeMonthLimit = checkHasThirtyDayAndThreeMonthLimit(
    spendingLimits?.limits || [],
  );

  if (loadingSpendingLimit || !activeSpendingLimit) return null;

  const daysSinceDecline = daysSinceStatusDate(application);

  // Don't let them refresh until 30 days after decline.
  // If we don't know how many days since decline, let them try
  const showRefreshButton =
    daysSinceDecline !== null ? daysSinceDecline >= 30 : true;

  return (
    <div className="max-sm:grid-cols-1 max-sm:gap-y-4 max-sm:[grid-template-areas:'left'_'bar'_'right'] grid grid-cols-[1fr_auto] gap-y-l [grid-template-areas:'left_right'_'bar_bar']">
      <div className="[grid-area:left]">
        <div className="max-sm:text-xl flex flex-wrap items-center gap-s text-2xl font-reg">
          You have <span className="font-bold">{formatCurrency(0)}</span> left
          to spend
        </div>
        <div className="text-s font-reg">
          We can't approve you for more funds right now &mdash; check back in 30
          days and hit 'Secure my limit'.
        </div>
      </div>
      <div className="max-sm:justify-self-start justify-self-end [grid-area:right]">
        <div className="gap-2 font-medium max-sm:justify-start max-sm:font-reg flex flex-wrap justify-end text-s">
          <div>Spending limit: {formatCurrency(0)}</div>
          {showRefreshButton && (
            <RefreshExpiredLimits
              loading={loading}
              setIsSpendingLimitDrawerOpen={setIsSpendingLimitDrawerOpen}
              refreshSpendingLimit={refreshSpendingLimit}
            />
          )}
        </div>
        <div className="max-sm:text-left text-right text-xs font-reg">
          {formatDuration({
            duration: activeSpendingLimit.duration_in_months,
            groupSameLimits: hasThirtyDayAndThreeMonthLimit,
          })}{' '}
          purchases{' '}
          <button
            onClick={() => {
              tracking.ampli.spendingLimitViewedOtherTerms();
              setIsSpendingLimitDrawerOpen(true);
            }}
            className="ml-xs cursor-pointer border-none bg-none p-0 text-[color:var(--color-primary-40,#4b4bff)] underline"
          >
            Other terms
          </button>
        </div>
      </div>
      <div className="[grid-area:bar]">
        <SpendingLimitBar status="declined" />
      </div>
    </div>
  );
};
